/**
 * Business Hooks
 *
 * React Query hooks for business portal operations.
 */

import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSdkContext } from '../providers/SdkProvider';
import { requireJourney } from './require-journey';
import { useAuth } from '../providers/AuthProvider';
import { talentInvitationKeys } from './useTalentInvitations';
import {
  createBusinessService,
  type TalentFilters,
  type CreateProgramInput,
  type CreateCohortInput,
  type UpdateProgramInput,
  type UpdateCohortInput,
  type AnalyticsFilters,
  type UpdateTalentInput,
  type UpdatePipelineStageInput,
  type TransitionTalentStageInput,
  type RequestInterviewFirstInput,
  type Program,
  type UpdateCompanyTaskInput,
  type UploadCompanyDocumentInput,
  type PoolSearchParams,
  type CareInboxParams,
  type VettingTalentsParams,
  type PipelineSummaryParams,
  type MobilityTalentsParams,
  type EvidenceTalentsParams,
  type AdaptationTalentsParams,
  type CareTalentsParams,
  type PoolSmartSearchRequest,
  type PoolFacetField,
  type PipelineRow,
  type TenantSettings,
  type ArchetypeCatalog,
  type PillarCatalog,
  type AffiliateOverview,
  type AffiliateOverviewParams,
  type AffiliatePlatformOverview,
  type AffiliateRedemptionList,
  type AffiliateRedemptionParams,
  type AffiliateExportParams,
  type AffiliatePayout,
} from '../services/business.service';
import type { SubmitObjectiveFeedbackInput } from '../services/care.service';
import type { AuditAction, CohortSnapshotView } from '@tonnie/types';
import type {
  CreateCareInterventionInput,
  UpdateCareInterventionInput,
  ResolveCareInterventionInput,
  AdaptationCoachingRequest,
  CareInterventionBatchInput,
  BusinessTeamRole,
} from '@tonnie/schemas';

// Query keys
export const businessKeys = {
  all: ['business'] as const,
  /**
   * The talent list, keyed by its filters.
   *
   * The filter element is APPENDED ONLY WHEN PRESENT. A trailing `undefined`
   * (`[...all, 'talents', undefined]`) is NOT a prefix of a scoped key
   * (`[...all, 'talents', { view: 'archived' }]`) under TanStack's
   * `partialMatchKey`, which walks `Object.keys` of the FILTER key — index `2`
   * included — and compares `undefined` against an object.
   *
   * So the no-arg form silently matched nothing scoped, and
   * `invalidateQueries({ queryKey: businessKeys.talents() })` — which is what
   * invite, invite-to-vetting, update-talent and update-stage all call — never
   * refreshed the Archived tab or any per-cohort list. (Archive and restore
   * escaped it only because they hand-write the literal `['business',
   * 'talents']` prefix.) The sibling `careOverview` key already carries this
   * exact note; the talents key never got the fix.
   */
  talents: (filters?: TalentFilters) =>
    filters
      ? ([...businessKeys.all, 'talents', filters] as const)
      : ([...businessKeys.all, 'talents'] as const),
  /**
   * The Vetting lens summary. Under the `talents` prefix on purpose: it is
   * counted from the talent rows, so every mutation that invalidates
   * `talents()` refreshes it too.
   */
  /** The Pipeline board's KPI row — under `talents`, so every talent write refreshes it. */
  pipelineSummary: (params: PipelineSummaryParams) =>
    [...businessKeys.all, 'talents', 'pipeline-summary', params] as const,
  vettingSummary: (filters?: { programId?: string; cohortId?: string }) =>
    [...businessKeys.all, 'talents', 'vetting-summary', filters ?? null] as const,
  /** A page of the Vetting list — under `talents` for the same reason. */
  vettingTalents: (params: VettingTalentsParams) =>
    [...businessKeys.all, 'talents', 'vetting-list', params] as const,
  /** A page of the Mobility list — under `talents` so talent writes refresh it. */
  mobilityTalents: (params: MobilityTalentsParams) =>
    [...businessKeys.all, 'talents', 'mobility-list', params] as const,
  /** A page of the Evidence passports — under `talents` so talent writes refresh it. */
  evidenceTalents: (params: EvidenceTalentsParams) =>
    [...businessKeys.all, 'talents', 'evidence-list', params] as const,
  /** A page of the Adaptation list — under `talents` so talent writes refresh it. */
  adaptationTalents: (params: AdaptationTalentsParams) =>
    [...businessKeys.all, 'talents', 'adaptation-list', params] as const,
  /** A page of the Care list — under `talents` so talent writes refresh it. */
  careTalents: (params: CareTalentsParams) =>
    [...businessKeys.all, 'talents', 'care-list', params] as const,
  /** The attention queue, scoped. (W7.2 · F28) */
  attentionQueue: (params?: { limit: number; programId?: string; cohortId?: string }) =>
    params
      ? ([...businessKeys.all, 'attention-queue', params] as const)
      : ([...businessKeys.all, 'attention-queue'] as const),
  /** One cohort's server-assembled roster snapshot. (W7.1 · F19) */
  cohortSnapshot: (
    cohortId: string,
    params?: { limit: number; offset?: number; view?: CohortSnapshotView }
  ) => [...businessKeys.all, 'cohort-snapshot', cohortId, params ?? null] as const,
  talent: (id: string) => [...businessKeys.all, 'talent', id] as const,
  // `view` is now part of the cache key so the Active / Archived tabs
  // each maintain their own cache entry. A single `invalidateQueries({
  // queryKey: [...businessKeys.all, 'programs'] })` cascades across
  // both views — TanStack matches prefix-wise.
  programs: (view?: 'active' | 'archived' | 'all') =>
    [...businessKeys.all, 'programs', view ?? 'active'] as const,
  program: (id: string) => [...businessKeys.all, 'program', id] as const,
  cohorts: (programId?: string, view?: 'active' | 'archived' | 'all') =>
    [...businessKeys.all, 'cohorts', programId ?? null, view ?? 'active'] as const,
  cohort: (id: string) => [...businessKeys.all, 'cohort', id] as const,
  analytics: (filters?: AnalyticsFilters) => [...businessKeys.all, 'analytics', filters] as const,
  engagement: (filters?: AnalyticsFilters) => [...businessKeys.all, 'engagement', filters] as const,
  meshAnalytics: (filters?: AnalyticsFilters) =>
    [...businessKeys.all, 'mesh-analytics', filters] as const,
  pipelineStats: (filters?: { programId?: string; cohortId?: string }) =>
    [...businessKeys.all, 'pipeline-stats', filters] as const,
  // Phase 3 keys
  vettingOverview: (filters?: { programId?: string; cohortId?: string }) =>
    [...businessKeys.all, 'vetting-overview', filters] as const,
  mobilityOverview: (filters?: {
    programId?: string;
    cohortId?: string;
    view?: 'active' | 'archived';
  }) =>
    // Appended only when present — see the note on `careOverview`.
    filters
      ? ([...businessKeys.all, 'mobility-overview', filters] as const)
      : ([...businessKeys.all, 'mobility-overview'] as const),
  companyTasks: (filters?: { talentId?: string; trackType?: string; status?: string }) =>
    [...businessKeys.all, 'company-tasks', filters] as const,
  adaptationOverview: (filters?: {
    programId?: string;
    cohortId?: string;
    view?: 'active' | 'archived';
  }) =>
    // Appended only when present — see the note on `careOverview`.
    filters
      ? ([...businessKeys.all, 'adaptation-overview', filters] as const)
      : ([...businessKeys.all, 'adaptation-overview'] as const),
  // NOTE: the optional filter/scope is APPENDED only when present. A trailing
  // `undefined` element (`[...all, key, undefined]`) is NOT a prefix of a scoped
  // key (`[...all, key, { programId }]`) under TanStack's partialMatchKey, so a
  // no-arg `invalidateQueries({ queryKey: careOverview() })` would silently fail
  // to invalidate the scoped queries. Omitting the element keeps the no-arg form
  // a true prefix that matches every scoped variant.
  careOverview: (filters?: {
    programId?: string;
    cohortId?: string;
    view?: 'active' | 'archived';
  }) =>
    filters
      ? ([...businessKeys.all, 'care-overview', filters] as const)
      : ([...businessKeys.all, 'care-overview'] as const),
  effectiveCareStrategy: (scope?: { programId?: string; cohortId?: string }) =>
    scope
      ? ([...businessKeys.all, 'effective-care-strategy', scope] as const)
      : ([...businessKeys.all, 'effective-care-strategy'] as const),
  careInterventionInbox: () => [...businessKeys.all, 'care-intervention-inbox'] as const,
  careInterventionComments: (id: string) =>
    [...businessKeys.all, 'care-intervention-comments', id] as const,
  careInterventionDetail: (id: string) =>
    [...businessKeys.all, 'care-intervention-detail', id] as const,
  evidenceOverview: (filters?: { programId?: string; cohortId?: string }) =>
    [...businessKeys.all, 'evidence-overview', filters] as const,
  // Phase 4 keys
  poolOverview: (filters?: { programId?: string; limit?: number }) =>
    [...businessKeys.all, 'pool-overview', filters] as const,
  poolSearch: (params?: PoolSearchParams) => [...businessKeys.all, 'pool-search', params] as const,
  poolSmartSearch: (params?: PoolSmartSearchRequest) =>
    [...businessKeys.all, 'pool-smart-search', params] as const,
  poolFacetOptions: (field: string, q: string) =>
    [...businessKeys.all, 'pool-facet-options', field, q] as const,
  poolSavedSearches: () => [...businessKeys.all, 'pool-saved-searches'] as const,
  poolTalent: (id: string) => [...businessKeys.all, 'pool-talent', id] as const,
  // Affiliate program (BL-213)
  affiliateStatus: () => [...businessKeys.all, 'affiliate-status'] as const,
  affiliateOverview: (params?: AffiliateOverviewParams) =>
    [...businessKeys.all, 'affiliate-overview', params] as const,
  affiliateRedemptions: (params?: AffiliateRedemptionParams) =>
    [...businessKeys.all, 'affiliate-redemptions', params] as const,
  affiliatePayouts: () => [...businessKeys.all, 'affiliate-payouts'] as const,
  affiliatePlatformOverview: () => [...businessKeys.all, 'affiliate-platform-overview'] as const,
  dashboard: (filters?: { programId?: string }) =>
    [...businessKeys.all, 'dashboard', filters] as const,
  activity: (params?: { limit?: number; since?: string }) =>
    [...businessKeys.all, 'activity', params] as const,
  // Phase 5 keys
  team: [...(['business'] as const), 'team'] as const,
  tenantSettings: () => [...businessKeys.all, 'tenant-settings'] as const,
  auditLogs: (filters?: {
    action?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) => [...businessKeys.all, 'audit-logs', filters] as const,
  meshScores: (talentIds: string[]) => [...businessKeys.all, 'mesh-scores', talentIds] as const,
  // Message keys
  messageTemplates: () => [...businessKeys.all, 'message-templates'] as const,
  messageAnalytics: () => [...businessKeys.all, 'message-analytics'] as const,
  // Catalog keys (Radar Tooltips, 2026-Q3) — keyed only by tenant
  // implicitly (the SDK client carries the auth context). No params
  // means the cache is session-stable and invalidates cleanly on
  // tenant switch (tokens cycle on switch, which forces a refetch via
  // the SDK auth interceptor).
  archetypeCatalog: () => [...businessKeys.all, 'catalogs', 'archetypes'] as const,
  pillarCatalog: () => [...businessKeys.all, 'catalogs', 'pillars'] as const,
  // Objectives-based feedback (BL-125)
  objectiveFeedbackDue: () => [...businessKeys.all, 'objective-feedback-due'] as const,
  talentObjectives: (talentId: string, journeyId: string | null) =>
    [...businessKeys.all, 'talent-objectives', talentId, journeyId] as const,
  objectivesOverview: () => [...businessKeys.all, 'objectives-overview'] as const,
};

// ═══════════════════════════════════════════════════════════════════════
// TALENT HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * THE scoped talent list. (W7.1 · DW7-1)
 *
 * ─── Why `view` and `limit` are required ──────────────────────────────────
 *
 * There was exactly one fetcher and twelve call sites. NINE of them passed no
 * limit and silently took the controller default of 20 — including the
 * portal-wide context behind Home, Vetting, Mobility, Adaptation, Care,
 * Evidence and Pool. No surface anywhere passed an offset, so nothing could
 * page. The dashboard computed total talents, health % and sponsored % over a
 * 20-row slice; five hero pages rendered `Active (N) | Archived (M)` where
 * BOTH numbers were `array.length` over capped arrays; the cohort page showed
 * a header count of 25 above 20 rows with no indication anything was missing.
 *
 * None of that was a decision anybody made. It is what an optional parameter
 * with a quiet default produces at the twelfth call site. Required, the
 * compiler asks — the same move `includePhotos` and `ArchiveScope` make on the
 * server, for the same reason: the defect class stops being representable
 * rather than stopping by vigilance.
 *
 * ─── Why the result is not the raw envelope ───────────────────────────────
 *
 * `rows.length` and `total` are different questions and the old shape let a
 * caller reach for the wrong one without noticing. So the fetched count is
 * named `fetched`, both tab badges arrive as `totals`, and `hasMore` is
 * derived once here instead of at each surface that might forget.
 *
 * ─── The cache key ────────────────────────────────────────────────────────
 *
 * Still `['business', 'talents', <filters>]`. That prefix is load-bearing:
 * `useArchiveJourney` and `useUnarchiveJourney` invalidate and optimistically
 * patch by the literal `['business', 'talents']` prefix, and the optimistic
 * archive filters cached pages by `journeyInstanceId`. Moving this key out
 * from under that prefix would leave the archive path silently not
 * invalidating — which is exactly the failure `businessKeys.talents()` already
 * had for its no-arg form (see the key factory above).
 */
export interface TalentListScope {
  /** REQUIRED. There is no default view — see the docblock. */
  view: 'active' | 'archived' | 'all';
  /** REQUIRED. There is no default page size — see the docblock. */
  limit: number;
  /** Rows to skip. Present so a surface CAN page; nothing could before. */
  offset?: number;
  programId?: string;
  cohortId?: string;
  search?: string;
  status?: 'active' | 'inactive' | 'pending';
  /**
   * Resolve avatars? Defaults to FALSE here, unlike the endpoint.
   *
   * The endpoint defaults to `true` because its historic default caller is the
   * board, which draws faces. This hook's callers overwhelmingly do not, and a
   * photo is one concurrent S3 presign per row — the reason nine surfaces were
   * afraid to ask for more than 20. A surface that draws a face says so.
   */
  photos?: boolean;
  /** Skip the fetch entirely (e.g. the id it is scoped to has not resolved). */
  enabled?: boolean;
}

export interface TalentListResult {
  rows: PipelineRow[];
  /** How many rows came back. NOT a population count — see `total`. */
  fetched: number;
  /** Rows matching `view`, server-side, ignoring `limit`. */
  total: number;
  /**
   * Both tab badges, server-supplied. Neither is ever `rows.length`. `null`
   * until the server has answered: an unknown count is not zero, and a badge
   * that read "0" while the list loaded was a wrong answer on screen.
   */
  totals: { active: number; archived: number } | null;
  /** `true` when the server holds rows this page did not fetch. */
  hasMore: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export function useTalentList(scope: TalentListScope): TalentListResult {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const businessService = createBusinessService(client);

  const filters: TalentFilters = {
    view: scope.view,
    limit: scope.limit,
    ...(scope.offset !== undefined ? { offset: scope.offset } : {}),
    ...(scope.programId ? { programId: scope.programId } : {}),
    ...(scope.cohortId ? { cohortId: scope.cohortId } : {}),
    ...(scope.search ? { search: scope.search } : {}),
    ...(scope.status ? { status: scope.status } : {}),
    photos: scope.photos ?? false,
  };

  const query = useQuery({
    queryKey: businessKeys.talents(filters),
    queryFn: () => businessService.getTalents(filters),
    enabled: isSignedIn && !!token && scope.enabled !== false,
    // Mirrors `useInvitations`. The Pipeline board draws Acquisition from
    // `useInvitations` and Vetting+ from this list; when a citizen accepts in
    // another tab BOTH go stale at once — the invitation leaves `pending` and a
    // talent row appears. Without the same refetch settings on both, the card
    // vanishes from Acquisition and never shows up in Vetting until a reload.
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

  const rows = query.data?.talents ?? [];
  const total = query.data?.total ?? 0;
  return {
    rows,
    fetched: rows.length,
    total,
    // The server ALWAYS sends `totals`, including on the empty-programme early
    // return; before it answers there is no count to show.
    totals: query.data?.totals ?? null,
    hasMore: (scope.offset ?? 0) + rows.length < total,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}

/**
 * The attention queue — "who needs me today", ranked by the server. (W7.2 · F28)
 *
 * `limit` is required for the same reason it is on `useTalentList`: the three
 * components this replaces sliced the queue to 3, 4 and 5 respectively, each
 * number written into JSX. The caller now states it and the server states the
 * total it was taken from.
 */
export function useAttentionQueue(params: {
  limit: number;
  programId?: string;
  cohortId?: string;
  enabled?: boolean;
}) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const businessService = createBusinessService(client);
  const scope = {
    limit: params.limit,
    ...(params.programId ? { programId: params.programId } : {}),
    ...(params.cohortId ? { cohortId: params.cohortId } : {}),
  };

  return useQuery({
    queryKey: businessKeys.attentionQueue(scope),
    queryFn: () => businessService.getAttentionQueue(scope),
    enabled: isSignedIn && !!token && params.enabled !== false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * One cohort's server-assembled ROSTER. (W7.1 · F19)
 *
 * The endpoint has existed since W3 and had no client at all — six URL
 * constants in `client/config.ts`, no method, no hook. Meanwhile the cohort
 * page mapped `getTalents` into a page-local row shape that de-duplicated by
 * PERSON (losing a multi-journey talent's second engagement), missed every
 * invitation that never produced a journey, and labelled the phase with seven
 * hardcoded English strings.
 *
 * `limit` is required here too, and the response carries `page.hasMore`, so a
 * roster can never be short without saying so.
 */
export function useCohortSnapshot(
  cohortId: string | undefined,
  /**
   * `view` is part of the cache key, so the Active and Archived tabs each keep
   * their own entry — and the `counts` either one returns badges both. (F22)
   */
  params: { limit: number; offset?: number; view?: CohortSnapshotView }
) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.cohortSnapshot(cohortId ?? '', params),
    queryFn: () => businessService.getCohortSnapshot(cohortId as string, params),
    enabled: isSignedIn && !!token && !!cohortId,
    refetchOnMount: 'always',
  });
}

/**
 * Get talent by ID. Optional `journeyId` disambiguates which journey
 * row to load when a talent has multiple journeys in the same tenant
 * (multi-opportunity scenario, archived re-invite). The query key
 * includes `journeyId` so different cards on the kanban don't share
 * cache and step on each other's data.
 */
export function useTalent(id: string, options?: { journeyId?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: [...businessKeys.talent(id), options?.journeyId ?? null] as const,
    queryFn: () => businessService.getTalent(id, options),
    enabled: !!id,
  });
}

/**
 * Invite talent
 */
export function useInviteTalent() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (input: {
      email: string;
      firstName?: string;
      lastName?: string;
      programId?: string;
      cohortId?: string;
    }) => businessService.inviteTalent(input),
    onSuccess: () => {
      // Talents list, cohort talentCount, and pipeline stats all
      // change after an invite (cohort talentCount derives from
      // talents.cohort_id since migration 0041).
      queryClient.invalidateQueries({ queryKey: businessKeys.talents() });
      queryClient.invalidateQueries({ queryKey: businessKeys.cohorts() });
      queryClient.invalidateQueries({ queryKey: businessKeys.pipelineStats() });
    },
  });
}

/**
 * Move a batch of talents to vetting stage. Backed by
 * `POST /api/business/talents/invite-to-vetting` — replaces the
 * client-side no-op the old InviteToVettingModal used to call.
 */
export function useInviteTalentsToVetting() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (input: {
      talentIds: string[];
      /** The relationships the ask is for — the selected cards. */
      journeyIds?: string[];
      message?: string;
      invitationKind?: 'full_checkup' | 'testit' | null;
      testitTests?: string[] | null;
      maxResultAgeDays?: number | null;
    }) => businessService.inviteTalentsToVetting(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.talents() });
      queryClient.invalidateQueries({ queryKey: businessKeys.pipelineStats() });
    },
  });
}

/**
 * Update talent
 */
export function useUpdateTalent() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTalentInput }) =>
      businessService.updateTalent(id, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: businessKeys.talent(variables.id) });
      queryClient.invalidateQueries({ queryKey: businessKeys.talents() });
    },
  });
}

/**
 * Update talent pipeline stage (with audit trail)
 */
export function useUpdatePipelineStage() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: ({ talentId, input }: { talentId: string; input: UpdatePipelineStageInput }) =>
      businessService.updatePipelineStage(talentId, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: businessKeys.talent(variables.talentId) });
      queryClient.invalidateQueries({ queryKey: businessKeys.talents() });
      queryClient.invalidateQueries({ queryKey: businessKeys.pipelineStats() });
    },
  });
}

/**
 * Soft-archive ONE journey (Vetting / Sponsored cards). Optimistic: the card
 * disappears the instant the user
 * confirms, and rolls back if the server rejects. The journey flips to
 * an archived lifecycle so the citizen sees it disappear from their
 * cross-tenant journey list.
 *
 * Addressed by `journeyId`, NOT by talent id. The pipeline renders one
 * card per JOURNEY, and a talent can hold several journeys in the same
 * tenant (re-invitation, multi-opportunity). The previous talent-id
 * variant made both halves of the round trip ambiguous: the server
 * archived whichever row it resolved first, and the optimistic update
 * below filtered the cache by talent id — so every card belonging to
 * that person vanished while `total` dropped by exactly one, leaving
 * the list and its count disagreeing until the next poll.
 *
 * `talentId` is carried alongside purely to evict that talent's detail
 * cache on success; it never addresses the mutation.
 */
export function useArchiveJourney() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: ({ journeyId, reason }: { journeyId: string; talentId: string; reason?: string }) =>
      businessService.archiveJourney(journeyId, { reason }),
    onMutate: async ({ journeyId }) => {
      await queryClient.cancelQueries({ queryKey: ['business', 'talents'] });
      const snapshots = queryClient.getQueriesData<unknown>({
        queryKey: ['business', 'talents'],
      });
      queryClient.setQueriesData<unknown>({ queryKey: ['business', 'talents'] }, (old: unknown) => {
        if (!old) return old;
        // `total` is decremented by the number of rows this update
        // ACTUALLY removed, never by a hardcoded 1 — otherwise a cache
        // page that doesn't hold the archived row still loses a count.
        if (Array.isArray(old)) {
          return (old as Array<{ journeyInstanceId?: string }>).filter(
            (t) => t?.journeyInstanceId !== journeyId
          );
        }
        if (typeof old === 'object' && old !== null && 'talents' in old) {
          const obj = old as { talents: Array<{ journeyInstanceId?: string }>; total?: number };
          const next = obj.talents.filter((t) => t?.journeyInstanceId !== journeyId);
          const removed = obj.talents.length - next.length;
          return {
            ...obj,
            talents: next,
            total: typeof obj.total === 'number' ? Math.max(0, obj.total - removed) : obj.total,
          };
        }
        return old;
      });
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const [queryKey, data] of ctx.snapshots) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSuccess: (_data, { talentId }) => {
      queryClient.removeQueries({ queryKey: businessKeys.talent(talentId) });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['business', 'talents'] });
      queryClient.invalidateQueries({ queryKey: ['business', 'pipeline-stats'] });
      queryClient.invalidateQueries({ queryKey: ['business', 'cohorts'] });
      queryClient.invalidateQueries({ queryKey: ['business', 'cohort'] });
    },
  });
}

/**
 * Restore (un-archive) a previously archived journey. Inverse of
 * `useArchiveJourney`.
 *
 * SAME-ROW reactivation: the journey id passed in IS the journey id that comes
 * back live. `JourneyStateService.restore()` clears the archive markers on the
 * existing row (`deletedAt` → NULL, `deleteReason` → NULL, `pipelineStage` →
 * `preArchiveStage`, `isPrimary` recomputed) and the company's
 * `journey_engagements` marker beside it, so every hero record — CheckUp,
 * iQulture, IRR, scores — and the whole `pipeline_transitions` history stay
 * attached because the row never moves.
 *
 * This docstring used to say the opposite: "the backend creates a NEW journey
 * row … the archived row is preserved as audit". It never did. The claim was
 * caught end-to-end in W7.1, by the first archive/restore round trip the suite
 * has ever run — a test written against the comment asserted the returned id
 * DIFFERED, and it does not. A doc that describes a design nobody built reads
 * exactly like one that describes the code.
 *
 * Cache invalidation strategy: blow up BOTH the active and archived
 * talents caches, so the card moves from the Archived tab back to
 * Active without a manual reload. Also invalidates pipeline stats so
 * the tab badges re-count.
 */
export function useUnarchiveJourney() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (journeyId: string) => businessService.unarchiveJourney(journeyId),
    onSettled: () => {
      // Invalidate all `['business', 'talents', ...]` query variants —
      // active view, archived view, and any per-cohort/program filtered
      // caches the kanban currently has open.
      queryClient.invalidateQueries({ queryKey: ['business', 'talents'] });
      queryClient.invalidateQueries({ queryKey: ['business', 'pipeline-stats'] });
      queryClient.invalidateQueries({ queryKey: ['business', 'cohorts'] });
      queryClient.invalidateQueries({ queryKey: ['business', 'cohort'] });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// PROGRAM HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get programs scoped to the caller's tenant.
 *
 * `opts.view` (default `'active'`) mirrors the Pipeline scope-tab
 * contract. Callers that need both views (e.g. the Programs page that
 * shows tab count badges) mount this hook twice — once for each view —
 * and the keyed cache holds them separately.
 *
 * Legacy callers calling `usePrograms()` without args see the same
 * data they always saw (active-only).
 */
export function usePrograms(opts: { view?: 'active' | 'archived' | 'all' } = {}) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const businessService = createBusinessService(client);
  const view = opts.view ?? 'active';

  return useQuery({
    queryKey: businessKeys.programs(view),
    queryFn: () => businessService.getPrograms({ view }),
    // Authed endpoint — don't fire before the token is on the client (mirrors
    // useSubscription). Lets BusinessContextProvider mount its inner provider
    // unconditionally instead of swapping element type on token-ready (which
    // remounted the whole routed subtree + reset page UI state).
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get program by ID
 */
export function useProgram(id: string) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.program(id),
    queryFn: () => businessService.getProgram(id),
    enabled: !!id,
  });
}

/**
 * Create program. Optimistically merges the new row into the `active`
 * view so the UI feels instant, then invalidates every program list
 * variant (`'active' | 'archived' | 'all'`) so other subscribers
 * (BusinessContext lookup, ProgramDetail breadcrumbs, dashboard
 * counts) pick the row up on their next read.
 *
 * Pre-fix the `setQueryData` was the only update — the `'all'` view
 * and the BusinessContext lookup (used by Pipeline / Cohorts filter
 * dropdowns) only refreshed on hard reload, so a freshly-created
 * program was missing from the dropdown until the admin navigated
 * away and back.
 */
export function useCreateProgram() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (input: CreateProgramInput) => businessService.createProgram(input),
    onSuccess: (newProgram) => {
      queryClient.setQueryData<Program[]>(businessKeys.programs('active'), (old) =>
        old ? [...old, newProgram] : [newProgram]
      );
      // Cascade across every program list scope so dropdowns, filter
      // chips, and the "All / Active / Archived" tab badges all see
      // the new row without a page reload.
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'programs'] });
    },
  });
}

/**
 * Update program (full mutable shape — name, description, status,
 * dates, etc.). Id is passed in the mutation args so list pages share
 * a single handler across many rows.
 *
 * Pre-2026-Q3 there was an unused `useUpdateProgram(id)` constructor
 * variant alongside this hook — removed per the standing dead-code
 * directive. Use this hook for every update path.
 */
export function useUpdateProgramById() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProgramInput }) =>
      businessService.updateProgram(id, input),
    onSuccess: (updatedProgram, variables) => {
      queryClient.setQueryData(businessKeys.program(variables.id), updatedProgram);
      // Cascade across all program list views (active + archived + all).
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'programs'] });
      // Pipeline counts include program rollup numbers — keep them honest.
      queryClient.invalidateQueries({ queryKey: businessKeys.pipelineStats() });
    },
  });
}

/**
 * Archive (soft-delete) program. Backend sets `status='archived'`;
 * the row stays queryable via the Archived scope tab.
 */
export function useArchiveProgram() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (id: string) => businessService.deleteProgram(id),
    onSuccess: () => {
      // Cascade across active + archived caches so the row moves between tabs.
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'programs'] });
      // Backend cascade-archives cohorts under the program (2026-Q3).
      // Invalidate the cohorts cache so the Cohorts list and the
      // per-cohort detail pages reflect the new status without a hard
      // reload. Bonus: `getCohort.phaseCounts` is derived from the
      // aggregator and doesn't change, but the cohort's `status`
      // badge does — invalidation refreshes both.
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'cohorts'] });
      // Pipeline counts drop the archived program's talents — refresh.
      queryClient.invalidateQueries({ queryKey: businessKeys.pipelineStats() });
    },
  });
}

/**
 * Inverse of `useArchiveProgram`. Calls the dedicated restore endpoint
 * which sets `status='active'`. Idempotent at the API layer.
 *
 * Asymmetric cascade rule (intentional): archive cascades to cohorts;
 * restore does NOT. The backend response includes `archivedCohortCount`
 * so the FE can surface a "N cohort(s) still archived" hint to the
 * admin. We still invalidate the cohorts cache so the program card's
 * `cohortCount` (which respects the view's active filter) refreshes.
 */
export function useRestoreProgram() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (id: string) => businessService.restoreProgram(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'programs'] });
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'cohorts'] });
      queryClient.invalidateQueries({ queryKey: businessKeys.pipelineStats() });
    },
  });
}

/**
 * @deprecated Use `useArchiveProgram` — the verb matches the actual
 * behavior (soft-delete via status='archived'). Kept as an alias for
 * one release so existing callers don't break.
 */
export const useDeleteProgram = useArchiveProgram;

// ═══════════════════════════════════════════════════════════════════════
// COHORT HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get cohorts scoped to the caller's tenant.
 *
 * Filters:
 *   • `opts.programId` — optional program scope (server-side filter)
 *   • `opts.view`      — scope tab selector; see `usePrograms` doc
 *
 * Backward compatible: legacy `useCohorts('prog-id')` calls (single
 * string arg) are NOT supported — those callers must migrate to
 * `useCohorts({ programId })`. The call sites that exist today all
 * route through this hook so the migration is local to this PR.
 */
export function useCohorts(
  opts: { programId?: string; view?: 'active' | 'archived' | 'all' } = {}
) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const businessService = createBusinessService(client);
  const view = opts.view ?? 'active';

  return useQuery({
    queryKey: businessKeys.cohorts(opts.programId, view),
    queryFn: () => businessService.getCohorts({ programId: opts.programId, view }),
    // Authed endpoint — gate on token like usePrograms/useSubscription.
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get cohort by ID
 */
export function useCohort(id: string) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.cohort(id),
    queryFn: () => businessService.getCohort(id),
    enabled: !!id,
  });
}

/**
 * Create cohort. Invalidates the cohorts cascade so every variant
 * (per-program filter, active/archived view, BusinessContext lookup
 * driving Pipeline's filter dropdown) refreshes. Also invalidates
 * `pipelineStats` and the parent program detail so counts stay in
 * sync without a reload.
 */
export function useCreateCohort() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (input: CreateCohortInput) => businessService.createCohort(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'cohorts'] });
      queryClient.invalidateQueries({ queryKey: businessKeys.pipelineStats() });
      if (variables.programId) {
        queryClient.invalidateQueries({
          queryKey: businessKeys.program(variables.programId),
        });
      }
    },
  });
}

/**
 * Update cohort. Accepts the full UpdateCohortInput shape (status,
 * dates, cadence, capacity, program reassignment).
 */
export function useUpdateCohort() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCohortInput }) =>
      businessService.updateCohort(id, input),
    onSuccess: (updatedCohort, variables) => {
      queryClient.setQueryData(businessKeys.cohort(variables.id), updatedCohort);
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'cohorts'] });
      queryClient.invalidateQueries({ queryKey: businessKeys.pipelineStats() });
    },
  });
}

/** Persist a Program-level Care-strategy override (Enterprise + admin only). */
export function useUpdateProgramCareStrategy() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: ({ id, careConfig }: { id: string; careConfig: Record<string, unknown> }) =>
      businessService.updateProgramCareStrategy(id, careConfig),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: businessKeys.program(variables.id) });
      queryClient.invalidateQueries({ queryKey: businessKeys.careOverview() });
      queryClient.invalidateQueries({ queryKey: businessKeys.effectiveCareStrategy() });
    },
  });
}

/** Persist a Cohort-level Care-strategy override (Enterprise + admin only). */
export function useUpdateCohortCareStrategy() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: ({ id, careConfig }: { id: string; careConfig: Record<string, unknown> }) =>
      businessService.updateCohortCareStrategy(id, careConfig),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: businessKeys.cohort(variables.id) });
      queryClient.invalidateQueries({ queryKey: businessKeys.careOverview() });
      queryClient.invalidateQueries({ queryKey: businessKeys.effectiveCareStrategy() });
    },
  });
}

/**
 * The resolved EFFECTIVE Care strategy for a config scope (Country ← Company ←
 * Program ← Cohort). Powers the inherited/effective config preview on the
 * Program + Cohort detail pages — the real cascade, never a placeholder.
 *
 * Scope semantics:
 *   • `undefined`            → disabled (no fetch). Callers pass this while the
 *                              scope ids are still loading (e.g. a cohort whose
 *                              `programId` hasn't resolved yet).
 *   • `{}`                   → COMPANY scope (Country ← Company). Used by the
 *                              Program detail page for the program's inherited
 *                              baseline (its parent IS the company).
 *   • `{ programId }`        → Company ← Program (a cohort's inherited baseline).
 *   • `{ programId?, cohortId }` → full cascade for a cohort scope.
 *
 * A bare `{}` is a meaningful (enabled) scope — only `undefined` disables.
 */
export function useEffectiveCareStrategy(scope?: { programId?: string; cohortId?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.effectiveCareStrategy(scope),
    queryFn: () => businessService.getEffectiveCareStrategy(scope),
    enabled: scope !== undefined,
  });
}

/**
 * Archive (soft-delete) cohort.
 */
export function useArchiveCohort() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (id: string) => businessService.deleteCohort(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'cohorts'] });
      queryClient.invalidateQueries({ queryKey: businessKeys.pipelineStats() });
    },
  });
}

/**
 * Inverse of `useArchiveCohort`. Calls the dedicated restore endpoint
 * which sets `status='active'`. Idempotent at the API layer.
 */
export function useRestoreCohort() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (id: string) => businessService.restoreCohort(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'cohorts'] });
      queryClient.invalidateQueries({ queryKey: businessKeys.pipelineStats() });
    },
  });
}

/**
 * @deprecated Use `useArchiveCohort` — the verb matches the actual
 * behavior. Kept as an alias for one release.
 */
export const useDeleteCohort = useArchiveCohort;

/**
 * Assign talents to cohort
 */
export function useAssignTalentsToCohort() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: ({
      cohortId,
      talentIds,
      journeyIds,
    }: {
      cohortId: string;
      talentIds: string[];
      /** The relationships being assigned — the selected cards. */
      journeyIds?: string[];
    }) => businessService.assignTalentsToCohort(cohortId, talentIds, journeyIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: businessKeys.cohort(variables.cohortId) });
      queryClient.invalidateQueries({ queryKey: businessKeys.talents() });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// ANALYTICS HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get analytics summary
 */
export function useAnalyticsSummary(filters?: AnalyticsFilters) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.analytics(filters),
    queryFn: () => businessService.getAnalyticsSummary(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get engagement analytics
 */
export function useEngagementAnalytics(
  filters?: AnalyticsFilters & { period?: 'day' | 'week' | 'month' }
) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.engagement(filters),
    queryFn: () => businessService.getEngagementAnalytics(filters),
  });
}

/**
 * Get MESH analytics
 */
export function useMeshAnalytics(
  filters?: AnalyticsFilters & { period?: 'day' | 'week' | 'month' }
) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.meshAnalytics(filters),
    queryFn: () => businessService.getMeshAnalytics(filters),
  });
}

/**
 * Get pipeline stats (counts per stage)
 */
export function usePipelineStats(filters?: { programId?: string; cohortId?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.pipelineStats(filters),
    queryFn: () => businessService.getPipelineStats(filters),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Get team members (for owner assignment)
 */
export function useTeamMembers() {
  const { client } = useSdkContext();

  return useQuery({
    queryKey: [...businessKeys.all, 'team-members'] as const,
    queryFn: async () => {
      return client.get<
        Array<{
          id: string;
          name: string;
          email: string;
          role: BusinessTeamRole;
          joinedAt: string;
        }>
      >('/api/business/team-members');
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 3: VETTING HOOKS
// ═══════════════════════════════════════════════════════════════════════

/** The Vetting lens, counted by the server over the whole roster (P2.1). */
export function useVettingTalents(params: VettingTalentsParams) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.vettingTalents(params),
    queryFn: () => businessService.getVettingTalents(params),
    // A page turn keeps the current page on screen until the next arrives.
    placeholderData: keepPreviousData,
  });
}

/** The Pipeline board's KPI row, counted by the server over the whole pipeline (D4). */
export function usePipelineSummary(params: PipelineSummaryParams) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.pipelineSummary(params),
    queryFn: () => businessService.getPipelineSummary(params),
    // A filter change keeps the current numbers on screen until the next arrive.
    placeholderData: keepPreviousData,
  });
}

export function useVettingSummary(filters?: { programId?: string; cohortId?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.vettingSummary(filters),
    queryFn: () => businessService.getVettingSummary(filters),
  });
}

export function useVettingOverview(filters?: { programId?: string; cohortId?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.vettingOverview(filters),
    queryFn: () => businessService.getVettingOverview(filters),
  });
}

export function useSponsorTalent() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: ({
      talentId,
      input,
    }: {
      talentId: string;
      input: {
        /** The journey of the relationship — required when the talent has several with this company. */
        journeyId?: string;
        programId?: string;
        cohortId?: string;
        /** Sponsor on a journey of this company's own (D-D). */
        dedicatedJourney?: boolean;
        branchName?: string;
        workLocation?: string;
        pricingPlan?: 'free' | 'enterprise';
        durationMonths?: number;
      };
    }) => businessService.sponsorTalent(talentId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.talents() });
      queryClient.invalidateQueries({ queryKey: businessKeys.vettingOverview() });
      queryClient.invalidateQueries({ queryKey: businessKeys.pipelineStats() });
      queryClient.invalidateQueries({ queryKey: businessKeys.cohorts() });
    },
  });
}

/**
 * Bulk-archive a batch of JOURNEYS (Pipeline bulk action bar).
 * Journey ids, matching the single-card archive — the selection is one
 * entry per CARD, and a card is a journey.
 */
export function useBulkArchiveJourneys() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (input: { journeyIds: string[]; reason?: string }) =>
      businessService.bulkArchiveJourneys(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.all });
    },
  });
}

/**
 * Bulk-sponsor a batch of talents into the same opportunity.
 */
export function useBulkSponsorTalents() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (input: {
      talentIds: string[];
      /** `journeyIds[i]` is `talentIds[i]`'s relationship. */
      journeyIds?: string[];
      cohortId?: string;
      programId?: string;
      branchName?: string;
      workLocation?: string;
      pricingPlan?: 'free' | 'enterprise';
      durationMonths?: number;
    }) => businessService.bulkSponsorTalents(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.all });
    },
  });
}

/**
 * Drag-and-drop driver: Acquisition → Vetting transition. Used by the
 * Pipeline kanban's drop targets. Sponsor stays on its own modal-driven
 * mutation because it requires the full opportunity payload.
 */
export function useTransitionTalentStage() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: ({ talentId, input }: { talentId: string; input: TransitionTalentStageInput }) =>
      businessService.transitionTalentStage(talentId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.talents() });
      queryClient.invalidateQueries({ queryKey: businessKeys.pipelineStats() });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// TONNIE SERVICE REQUESTS HOOKS — "Ask Tonnie Services"
// ═══════════════════════════════════════════════════════════════════════

const tonnieServiceRequestKeys = {
  all: ['business', 'service-requests'] as const,
  list: (filters?: { status?: string; limit?: number; offset?: number }) =>
    [...tonnieServiceRequestKeys.all, 'list', filters] as const,
};

export function useTonnieServiceRequests(filters?: {
  status?: 'submitted' | 'in_review' | 'proposal_sent' | 'accepted' | 'declined' | 'closed';
  limit?: number;
  offset?: number;
}) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: tonnieServiceRequestKeys.list(filters),
    queryFn: () => businessService.listTonnieServiceRequests(filters),
  });
}

export function useCreateTonnieServiceRequest() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (input: {
      scopes: string[];
      description: string;
      timeline?: string;
      budgetRange?: string;
      cohortId?: string;
      programId?: string;
      talentIds?: string[];
    }) => businessService.createTonnieServiceRequest(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tonnieServiceRequestKeys.all });
    },
  });
}

export function useCancelTonnieServiceRequest() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (id: string) => businessService.cancelTonnieServiceRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tonnieServiceRequestKeys.all });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// SPONSORSHIP PRICING — replaces hardcoded constant in @tonnie/foundation
// ═══════════════════════════════════════════════════════════════════════

export function useSponsorshipPricing() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: ['business', 'sponsorship-pricing'] as const,
    queryFn: () => businessService.getSponsorshipPricing(),
    // Pricing changes very rarely; cache for 5 minutes.
    staleTime: 5 * 60 * 1000,
  });
}

// ═══════════════════════════════════════════════════════════════════════
// TALENT DETAIL — per-hero admin hooks
// ═══════════════════════════════════════════════════════════════════════

const talentDetailKeys = {
  activity: (id: string, journeyId?: string, limit?: number) =>
    [...businessKeys.all, 'talent', id, 'activity', journeyId ?? null, limit ?? null] as const,
  /**
   * The activity key WITHOUT its `limit` segment — for partial matching.
   *
   * This exists because of a live bug, not for tidiness. `activity()` takes
   * three arguments and the reader passes all three (`TalentActivityTab` asks
   * for 50, `RecentActivityPreview` for 8), while the SSE stream called it with
   * TWO. The missing argument became `limit ?? null`, so every pushed frame was
   * written to `[…, 'activity', journeyId, null]` — a cache entry no component
   * subscribes to. The stream was connected, authenticated, receiving events,
   * and completely invisible; the feed only ever moved on the 60s poll.
   *
   * Prefix-matching fixes it for every limit at once, including limits added
   * later, which an exact key cannot.
   */
  activityPrefix: (id: string, journeyId?: string) =>
    [...businessKeys.all, 'talent', id, 'activity', journeyId ?? null] as const,
  checkup: (id: string, journeyId?: string) =>
    [...businessKeys.all, 'talent', id, 'checkup', journeyId ?? null] as const,
  // Full Brain CheckUp DTO — separate cache key from `checkup` (which
  // is the slim per-hero detail used by the Sponsored drawer's tab).
  // The Vetting drawer's rich content (MESH, Pillars, Skills, Pitch,
  // Archetypes, Diagnosis, Top Alerts, Plan90Days) all flow from here.
  checkupResults: (id: string, journeyId?: string) =>
    [...businessKeys.all, 'talent', id, 'checkup-results', journeyId ?? null] as const,
  mobility: (id: string, journeyId?: string) =>
    [...businessKeys.all, 'talent', id, 'mobility', journeyId ?? null] as const,
  adaptation: (id: string, journeyId?: string) =>
    [...businessKeys.all, 'talent', id, 'adaptation', journeyId ?? null] as const,
  care: (id: string, journeyId?: string) =>
    [...businessKeys.all, 'talent', id, 'care', journeyId ?? null] as const,
  evidence: (id: string, journeyId?: string) =>
    [...businessKeys.all, 'talent', id, 'evidence', journeyId ?? null] as const,
};

export function useTalentActivity(
  talentId: string,
  options?: { journeyId?: string; limit?: number }
) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  return useQuery({
    queryKey: talentDetailKeys.activity(talentId, options?.journeyId, options?.limit),
    queryFn: () => businessService.getTalentActivity(talentId, options),
    enabled: !!talentId,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useTalentCheckup(talentId: string, options?: { journeyId?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  return useQuery({
    queryKey: talentDetailKeys.checkup(talentId, options?.journeyId),
    queryFn: () => businessService.getTalentCheckup(talentId, options),
    enabled: !!talentId,
  });
}

/**
 * Full Brain CheckUp DTO for the admin Vetting drawer (rich-content
 * surface — MESH per-dimension scores, evaluation pillars, skills
 * thermometer, cultural archetypes, pitch video analysis + playback
 * URL, talent + company diagnosis, plan-90-days). Returns null when
 * the talent's CheckUp hasn't been calculated yet.
 *
 * Uses the same DTO journey-web's `/checkup/results` page consumes
 * via the citizen endpoint — we delegate to the same transform on
 * the backend, with tenant-ownership enforced by
 * `TalentDetailService.resolveJourney` BEFORE the citizen-service
 * runs (so the userId-based ownership check is bypassed safely).
 */
export function useTalentCheckupResults(talentId: string, options?: { journeyId?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  // ── L2 · F26 — no snapshot poll here any more. Whether a calculation is
  // running is the SERVER's answer (`useTalentCheckupCalculation`), which also
  // invalidates this query the moment the job finishes, whoever started it. ──
  return useQuery({
    queryKey: talentDetailKeys.checkupResults(talentId, options?.journeyId),
    queryFn: () => businessService.getTalentCheckupResults(talentId, options),
    enabled: !!talentId,
  });
}

/**
 * Admin "Re-run pitch analysis" / "Force recalculate" trigger.
 *
 * Counterpart to citizen-side `useTriggerCalculation` from
 * `useCheckUp.ts`, BUT routed through the tenant-isolated business
 * endpoint (`POST /api/business/talents/:id/checkup/recalculate`).
 * The citizen route 500s for admins because it enforces
 * `talent.user_id === ctx.userId` — admins never own the talent. The
 * business route delegates to `TalentDetailService.resolveJourney` to
 * verify tenant ownership and then triggers the calculation with
 * `force=true` server-side (admins always force).
 *
 * On success, invalidates the per-talent checkup + activity queries so
 * the FE re-fetches and reflects the new `calculating` job state.
 */
export function useRerunTalentCheckup(talentId: string, journeyId?: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: () => businessService.recalculateTalentCheckup(talentId, { journeyId }),
    // ── L2 · F26 — RETURNED, so the mutation stays pending until the checkup
    // DTO has been re-read and says the job is queued. Fire-and-forget left a
    // window between the POST settling and that read landing in which the
    // Re-run button was enabled again. The activity key is the PREFIX: an
    // exact key with no limit matched no reader's cached entry. ──
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: talentDetailKeys.checkupResults(talentId, journeyId),
        }),
        queryClient.invalidateQueries({
          queryKey: talentDetailKeys.checkup(talentId, journeyId),
        }),
        queryClient.invalidateQueries({
          queryKey: talentDetailKeys.activityPrefix(talentId, journeyId),
        }),
      ]),
  });
}

export function useTalentMobility(talentId: string, options?: { journeyId?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  return useQuery({
    queryKey: talentDetailKeys.mobility(talentId, options?.journeyId),
    queryFn: () => businessService.getTalentMobility(talentId, options),
    enabled: !!talentId,
  });
}

export function useTalentAdaptation(talentId: string, options?: { journeyId?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  return useQuery({
    queryKey: talentDetailKeys.adaptation(talentId, options?.journeyId),
    queryFn: () => businessService.getTalentAdaptation(talentId, options),
    enabled: !!talentId,
  });
}

export function useTalentCare(talentId: string, options?: { journeyId?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  return useQuery({
    queryKey: talentDetailKeys.care(talentId, options?.journeyId),
    queryFn: () => businessService.getTalentCare(talentId, options),
    enabled: !!talentId,
  });
}

export function useTalentEvidence(talentId: string, options?: { journeyId?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  return useQuery({
    queryKey: talentDetailKeys.evidence(talentId, options?.journeyId),
    queryFn: () => businessService.getTalentEvidence(talentId, options),
    enabled: !!talentId,
  });
}

export function useRequestInterviewFirst() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);
  return useMutation({
    mutationFn: ({ talentId, input }: { talentId: string; input: RequestInterviewFirstInput }) =>
      businessService.requestInterviewFirst(talentId, input),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'talent', vars.talentId] });
    },
  });
}

/**
 * Unified Care intervention create (R1). One taxonomy + lifecycle for every
 * intervention — backed by `POST /api/business/care/interventions`. Input is
 * `createCareInterventionSchema` from `@tonnie/schemas` (talentId lives inside
 * the payload). Invalidates the talent-detail care key + the Care overview so
 * the HR list refreshes.
 */
export function useCreateCareIntervention() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);
  return useMutation({
    mutationFn: (input: CreateCareInterventionInput) =>
      businessService.createCareIntervention(input),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: talentDetailKeys.care(vars.talentId) });
      queryClient.invalidateQueries({ queryKey: talentDetailKeys.activity(vars.talentId) });
      queryClient.invalidateQueries({ queryKey: businessKeys.careOverview() });
      queryClient.invalidateQueries({ queryKey: businessKeys.careInterventionInbox() });
    },
  });
}

/** Resolve an intervention with an outcome (+ optional resolution note). */
export function useResolveCareIntervention() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);
  return useMutation({
    mutationFn: ({
      id,
      talentId: _talentId,
      input,
    }: {
      id: string;
      /** Used for cache invalidation only — the route is keyed by `id`. */
      talentId?: string;
      input: ResolveCareInterventionInput;
    }) => businessService.resolveCareIntervention(id, input),
    onSuccess: (_data, vars) => {
      if (vars.talentId) {
        queryClient.invalidateQueries({ queryKey: talentDetailKeys.care(vars.talentId) });
        queryClient.invalidateQueries({ queryKey: talentDetailKeys.activity(vars.talentId) });
      }
      queryClient.invalidateQueries({ queryKey: businessKeys.careOverview() });
      queryClient.invalidateQueries({ queryKey: businessKeys.careInterventionInbox() });
      queryClient.invalidateQueries({ queryKey: businessKeys.careInterventionDetail(vars.id) });
    },
  });
}

/** Cancel a scheduled/pending intervention (no outcome). */
export function useCancelCareIntervention() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);
  return useMutation({
    mutationFn: ({ id, talentId: _talentId }: { id: string; talentId?: string }) =>
      businessService.cancelCareIntervention(id),
    onSuccess: (_data, vars) => {
      if (vars.talentId) {
        queryClient.invalidateQueries({ queryKey: talentDetailKeys.care(vars.talentId) });
        queryClient.invalidateQueries({ queryKey: talentDetailKeys.activity(vars.talentId) });
      }
      queryClient.invalidateQueries({ queryKey: businessKeys.careOverview() });
      queryClient.invalidateQueries({ queryKey: businessKeys.careInterventionInbox() });
    },
  });
}

/** Edit the descriptive fields of an open Care interaction (type/title/desc/priority/link). */
export function useUpdateCareIntervention() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);
  return useMutation({
    mutationFn: ({
      id,
      talentId: _talentId,
      input,
    }: {
      id: string;
      talentId?: string;
      input: UpdateCareInterventionInput;
    }) => businessService.updateCareIntervention(id, input),
    onSuccess: (_data, vars) => {
      if (vars.talentId) {
        queryClient.invalidateQueries({ queryKey: talentDetailKeys.care(vars.talentId) });
      }
      queryClient.invalidateQueries({ queryKey: businessKeys.careInterventionInbox() });
      queryClient.invalidateQueries({ queryKey: businessKeys.careInterventionDetail(vars.id) });
    },
  });
}

/** Reschedule a Care interaction to a new date/time. */
export function useRescheduleCareIntervention() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);
  return useMutation({
    mutationFn: ({
      id,
      talentId: _talentId,
      input,
    }: {
      id: string;
      talentId?: string;
      input: { scheduledAt: string };
    }) => businessService.rescheduleCareIntervention(id, input),
    onSuccess: (_data, vars) => {
      if (vars.talentId) {
        queryClient.invalidateQueries({ queryKey: talentDetailKeys.care(vars.talentId) });
      }
      queryClient.invalidateQueries({ queryKey: businessKeys.careInterventionInbox() });
      queryClient.invalidateQueries({ queryKey: businessKeys.careInterventionDetail(vars.id) });
    },
  });
}

/** Delegate (or clear) the owner of a Care interaction. */
export function useReassignCareIntervention() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);
  return useMutation({
    mutationFn: ({
      id,
      talentId: _talentId,
      input,
    }: {
      id: string;
      talentId?: string;
      input: { assignedTo: string | null };
    }) => businessService.reassignCareIntervention(id, input),
    onSuccess: (_data, vars) => {
      if (vars.talentId) {
        queryClient.invalidateQueries({ queryKey: talentDetailKeys.care(vars.talentId) });
      }
      queryClient.invalidateQueries({ queryKey: businessKeys.careInterventionInbox() });
      queryClient.invalidateQueries({ queryKey: businessKeys.careInterventionDetail(vars.id) });
    },
  });
}

/** The internal collaboration thread for a Care interaction. */
export function useCareInterventionComments(id: string) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  return useQuery({
    queryKey: businessKeys.careInterventionComments(id),
    queryFn: () => businessService.getCareInterventionComments(id),
    enabled: !!id,
  });
}

/** Append a comment to a Care interaction's internal thread. */
export function useAddCareInterventionComment() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: { content: string } }) =>
      businessService.addCareInterventionComment(id, input),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: businessKeys.careInterventionComments(vars.id),
      });
    },
  });
}

/**
 * Build a download URL for the per-talent PDF/CSV report. Not a hook —
 * just a helper that calls the SDK's URL builder. Components consume
 * it via an anchor `href` to trigger the browser download flow.
 */
/**
 * Download a cohort's talents as a CSV file.
 *
 * A mutation, not a query: it has a side effect the user initiates (a file
 * lands in Downloads) and must never run on mount or be re-run by a cache
 * revalidation.
 */
export function useDownloadCohortReportCsv() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: async (cohortId: string) => businessService.downloadCohortReportCsv(cohortId),
  });
}

/**
 * Download one talent's report (PDF or CSV) as a Blob.
 *
 * A mutation, not a query: it is an operator ACTION, and a query would refetch
 * a multi-megabyte PDF on every window focus. Replaced `useTalentReportUrl`,
 * which handed the caller a bare root-relative string for an `<a download>` —
 * wrong origin and no bearer token, so it could never have worked (H10).
 */
export function useDownloadTalentReport() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: async (vars: { talentId: string; format: 'pdf' | 'csv'; journeyId?: string }) =>
      businessService.downloadTalentReport(vars.talentId, vars.format, {
        journeyId: vars.journeyId,
      }),
  });
}

/**
 * SSE stream subscription for the activity feed. Returns the latest
 * snapshot and merges incoming events into the same TanStack Query
 * cache used by `useTalentActivity` so consumers don't double-fetch.
 *
 * Transport is `fetch` + ReadableStream rather than `EventSource`
 * (BL-017): the platform authenticates with a Clerk Bearer token, and
 * `EventSource` cannot send an `Authorization` header — every stream
 * attempt 401'd and the feed silently degraded to 60s polling. Fetch
 * lets us attach the header (no token-in-URL leakage into proxy logs)
 * and parse the event-stream frames ourselves.
 *
 * The hook stays defensive — on stream failure it marks disconnected,
 * schedules a reconnect, and the component still gets the polled list
 * from `useTalentActivity` (60s) in the meantime.
 */
const SSE_RECONNECT_DELAY_MS = 15_000;

/**
 * Minimal SSE frame parser: feeds chunks, emits `(event, data)` per
 * dispatched frame. Handles multi-line `data:` and comment lines
 * (`:keepalive` heartbeats are ignored by emitting nothing).
 */
export function createSseFrameParser(onEvent: (event: string, data: string) => void) {
  let buffer = '';
  let eventName = 'message';
  let dataLines: string[] = [];

  const dispatch = () => {
    if (dataLines.length > 0) {
      onEvent(eventName, dataLines.join('\n'));
    }
    eventName = 'message';
    dataLines = [];
  };

  return (chunk: string) => {
    buffer += chunk;
    let newlineIdx: number;
    while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, newlineIdx).replace(/\r$/, '');
      buffer = buffer.slice(newlineIdx + 1);
      if (line === '') {
        dispatch();
      } else if (line.startsWith(':')) {
        // comment / heartbeat — keepalive only
      } else if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
      }
    }
  };
}

export function useTalentActivityStream(
  talentId: string,
  options?: { journeyId?: string }
): { isConnected: boolean; lastEventAt: string | null } {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const [lastEventAt, setLastEventAt] = useState<string | null>(null);

  useEffect(() => {
    if (!talentId || typeof fetch === 'undefined') {
      return;
    }
    // Build the SSE URL through the WHATWG `URL` resolver so it behaves
    // identically to the rest of the SDK (`buildUrl()` in `client/base.ts`).
    // Naive `${baseUrl}/api/...` concatenation breaks when the configured
    // `baseUrl` already ends in `/api` (e.g. business-web's
    // `VITE_API_URL=http://localhost:4000/api`) — the result was
    // `…/api/api/business/…` and the SSE controller 404'd. Using
    // `new URL('/api/business/…', baseUrl)` makes the path root-relative
    // so it always resolves against the host regardless of whether the
    // baseUrl carries a path prefix.
    const baseUrl = client.getBaseUrl?.() ?? '';
    const params = new URLSearchParams();
    if (options?.journeyId) params.set('journeyId', options.journeyId);
    const qs = params.toString();
    const path = `/api/business/talents/${talentId}/activity/stream${qs ? `?${qs}` : ''}`;
    let url: string;
    try {
      url = new URL(path, baseUrl).toString();
    } catch {
      // Defensive fallback for malformed baseUrl in tests / SSR.
      url = `${baseUrl.replace(/\/+$/, '')}${path}`;
    }

    let disposed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const controller = new AbortController();

    /**
     * Update EVERY cached page of this talent's feed, at its own page size.
     *
     * Two things had to change together here. The write used to target one
     * EXACT key built from two arguments (`activity(id, journeyId)`) while
     * every reader builds it from three — `TalentActivityTab` with 50,
     * `RecentActivityPreview` with 8 — so the missing argument became
     * `limit ?? null` and each frame landed in an entry nothing subscribes to.
     * And the merge then clipped to a hard-coded 50, which is not any reader's
     * limit.
     *
     * So: match by PREFIX (which reaches every page size, including one added
     * later), and take each entry's OWN limit from the last segment of its key
     * — the segment the prefix deliberately stops short of.
     */
    const updateCachedFeeds = (
      merge: (
        prev: import('../services/business.service').TalentActivityFeed,
        limit: number
      ) => import('../services/business.service').TalentActivityFeed
    ) => {
      const prefix = talentDetailKeys.activityPrefix(talentId, options?.journeyId);
      for (const query of queryClient.getQueryCache().findAll({ queryKey: prefix })) {
        const tail = query.queryKey[query.queryKey.length - 1];
        const limit = typeof tail === 'number' ? tail : 50;
        queryClient.setQueryData(
          query.queryKey,
          (prev: import('../services/business.service').TalentActivityFeed | undefined) =>
            prev ? merge(prev, limit) : prev
        );
      }
    };

    const handleEvent = (event: string, data: string) => {
      if (event === 'snapshot') {
        try {
          const snapshot = JSON.parse(
            data
          ) as import('../services/business.service').TalentActivityFeed;
          updateCachedFeeds((prev, limit) => {
            // MERGE, don't replace. The snapshot is the server's latest 25 and
            // a cached entry may legitimately hold more, so overwriting would
            // truncate a list the reader is looking at. `prev` keeps its own
            // `nextCursor` because that cursor anchors ITS page, not the
            // snapshot's.
            const seen = new Set(snapshot.events.map((e) => e.id));
            const events = [...snapshot.events, ...prev.events.filter((e) => !seen.has(e.id))].sort(
              (a, b) => (a.occurredAt < b.occurredAt ? 1 : a.occurredAt > b.occurredAt ? -1 : 0)
            );
            return { ...prev, events: events.slice(0, limit) };
          });
        } catch {
          /* drop malformed payload */
        }
      } else if (event === 'activity') {
        try {
          const incoming = JSON.parse(data);
          setLastEventAt(new Date().toISOString());
          updateCachedFeeds((prev, limit) => {
            // Dedupe by id so re-deliveries don't double-render.
            if (prev.events.some((e) => e.id === incoming.id)) return prev;
            return {
              ...prev,
              events: [incoming, ...prev.events].slice(0, limit),
            };
          });
        } catch {
          /* drop */
        }
      }
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      setIsConnected(false);
      reconnectTimer = setTimeout(() => void connect(), SSE_RECONNECT_DELAY_MS);
    };

    const connect = async (): Promise<void> => {
      try {
        const token = await client.getFreshAuthToken?.();
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          signal: controller.signal,
        });
        if (!response.ok || !response.body) {
          scheduleReconnect();
          return;
        }

        setIsConnected(true);
        const parse = createSseFrameParser(handleEvent);
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          parse(decoder.decode(value, { stream: true }));
        }
        // Server closed the stream — reconnect unless we're unmounting.
        scheduleReconnect();
      } catch {
        // Abort on unmount lands here too; scheduleReconnect no-ops then.
        scheduleReconnect();
      }
    };

    void connect();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      controller.abort();
      setIsConnected(false);
    };
  }, [client, queryClient, talentId, options?.journeyId]);

  return { isConnected, lastEventAt };
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 3: MOBILITY HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function useMobilityTalents(params: MobilityTalentsParams) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.mobilityTalents(params),
    queryFn: () => businessService.getMobilityTalents(params),
    placeholderData: keepPreviousData,
  });
}

export function useMobilityOverview(filters?: {
  programId?: string;
  cohortId?: string;
  view?: 'active' | 'archived';
}) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.mobilityOverview(filters),
    queryFn: () => businessService.getMobilityOverview(filters),
  });
}

export function useCompanyTasks(filters?: {
  talentId?: string;
  trackType?: string;
  status?: string;
}) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.companyTasks(filters),
    queryFn: () => businessService.getCompanyTasks(filters),
  });
}

export function useUpdateCompanyTask() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCompanyTaskInput }) =>
      businessService.updateCompanyTask(id, input),
    onSuccess: () => {
      // Prefix-match ALL company-task queries (unfiltered list on the Mobility
      // page AND the per-talent `{talentId}` query in the Sponsored drawer).
      // `companyTasks()` would append a trailing `undefined` that does NOT
      // partial-match the `{talentId}` key, leaving the drawer stale.
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'company-tasks'] });
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'mobility-overview'] });
    },
  });
}

export function useUploadCompanyDocument() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (input: UploadCompanyDocumentInput) => businessService.uploadCompanyDocument(input),
    onSuccess: () => {
      // Prefix-match every variant (see useUpdateCompanyTask) — an uploaded doc
      // lives under its task, and the Mobility counts include it.
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'company-tasks'] });
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'mobility-overview'] });
    },
  });
}

/**
 * R1 final push · F1-SDK — request a presigned S3 PUT URL for a
 * Mobility document upload. Mutation (not query) because each call
 * issues a brand-new short-lived URL; the FE shouldn't cache them.
 */
export function useGetMobilityDocumentUploadUrl() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (input: {
      /** The kit document this upload fills — it names the journey. */
      documentId: string;
      trackType: string;
      fileName: string;
      fileType: string;
      fileSize: number;
    }) => businessService.getMobilityDocumentUploadUrl(input),
  });
}

/** BL-090 — presigned URL to download/verify ONE of a talent's mobility documents. */
export function useMobilityDocumentDownloadUrl() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (input: { talentId: string; documentId: string; journeyId?: string }) =>
      businessService.getMobilityDocumentDownloadUrl(input.talentId, input.documentId, {
        journeyId: input.journeyId,
      }),
  });
}

/** BL-090 — download the selected mobility documents as a single ZIP blob. */
export function useDownloadMobilityDocumentsZip() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (input: { talentId: string; documentIds: string[]; journeyId?: string }) =>
      businessService.downloadMobilityDocumentsZip(input.talentId, {
        documentIds: input.documentIds,
        journeyId: input.journeyId,
      }),
  });
}

/**
 * R1 final push · F2-SDK — fire a Bell reminder for a Company
 * Action. Invalidates the companyTasks query on success so the
 * row's `lastRemindedAt` metadata refreshes; the FE can then dim
 * the Bell icon and show a "Recently reminded" tooltip.
 */
export function useRemindCompanyTask() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (taskId: string) => businessService.remindCompanyTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'company-tasks'] });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 3: ADAPTATION HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function useAdaptationOverview(filters?: {
  programId?: string;
  cohortId?: string;
  view?: 'active' | 'archived';
}) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.adaptationOverview(filters),
    queryFn: () => businessService.getAdaptationOverview(filters),
  });
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 3: CARE HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function useCareOverview(filters?: {
  programId?: string;
  cohortId?: string;
  view?: 'active' | 'archived';
}) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.careOverview(filters),
    queryFn: () => businessService.getCareOverview(filters),
  });
}

/** Cross-talent Care interactions inbox — `open` (default), `done`, or `all`; `mine` scopes to the caller. */
export function useCareInterventionInbox(params: CareInboxParams) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: [...businessKeys.careInterventionInbox(), params] as const,
    queryFn: () => businessService.getCareInterventionInbox(params),
    // A page turn or a new search keeps the current page on screen until the
    // next answer arrives.
    placeholderData: keepPreviousData,
  });
}

/** Full detail for one Care interaction (the detail drawer). */
export function useCareInterventionDetail(id: string | null) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: id ? businessKeys.careInterventionDetail(id) : ['care-intervention-detail', 'none'],
    queryFn: () => businessService.getCareInterventionDetail(id as string),
    enabled: !!id,
  });
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 3: EVIDENCE HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function useCareTalents(params: CareTalentsParams) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.careTalents(params),
    queryFn: () => businessService.getCareTalents(params),
    placeholderData: keepPreviousData,
  });
}

/**
 * One Care plan for a selection (D3) — the ticked engagements, or every one
 * matching the list's filters. The server reaches only those that need an
 * intervention; the lens, its list and the interactions inbox refresh.
 */
export function useCreateCareInterventionBatch() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CareInterventionBatchInput) =>
      businessService.createCareInterventionBatch(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.careOverview() });
      void queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'talents'] });
      void queryClient.invalidateQueries({ queryKey: businessKeys.careInterventionInbox() });
    },
  });
}

export function useAdaptationTalents(params: AdaptationTalentsParams) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.adaptationTalents(params),
    queryFn: () => businessService.getAdaptationTalents(params),
    placeholderData: keepPreviousData,
  });
}

/**
 * Coach one talent, a group or a band (`POST /business/adaptation/coaching`).
 * The server picks the audience and each talent's action; each coaching is a
 * Care interaction, so the lens, its list and the interactions inbox refresh.
 */
export function useCoachAdaptation() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: AdaptationCoachingRequest) => businessService.coachAdaptation(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: businessKeys.adaptationOverview() });
      void queryClient.invalidateQueries({ queryKey: [...businessKeys.all, 'talents'] });
      void queryClient.invalidateQueries({ queryKey: businessKeys.careInterventionInbox() });
    },
  });
}

export function useEvidenceTalents(params: EvidenceTalentsParams) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.evidenceTalents(params),
    queryFn: () => businessService.getEvidenceTalents(params),
    placeholderData: keepPreviousData,
  });
}

export function useEvidenceOverview(filters?: { programId?: string; cohortId?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.evidenceOverview(filters),
    queryFn: () => businessService.getEvidenceOverview(filters),
  });
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 4: POOL HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function usePoolOverview(filters?: { programId?: string; limit?: number }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.poolOverview(filters),
    queryFn: () => businessService.getPoolOverview(filters),
    staleTime: 1000 * 60 * 5,
  });
}

export function usePoolSearch(params: PoolSearchParams, options?: { enabled?: boolean }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  // Fire when any criterion is set (note: `locations` is the multi-city field —
  // omitting it here is what silently dropped location-only searches). Callers
  // can force it on via `options.enabled` to browse the FULL pool with no
  // criteria (the Pool page), instead of falling back to the recommended set.
  const hasCriteria = !!(
    params.q ||
    params.location ||
    params.locations?.length ||
    params.skills?.length ||
    params.iqultureBadges?.length ||
    params.roles?.length
  );

  return useQuery({
    queryKey: businessKeys.poolSearch(params),
    queryFn: () => businessService.searchPool(params),
    enabled: options?.enabled ?? hasCriteria,
    // A page turn keeps the current page (and its total) on screen until the
    // next one arrives, rather than flashing an empty grid.
    placeholderData: keepPreviousData,
  });
}

/**
 * Smart (Job-Description) pool search (BL-207). Keyed on the whole request
 * (JD + filters + page) so pages cache independently. Enabled only when a JD
 * is present, unless the caller forces it.
 */
export function usePoolSmartSearch(
  request: PoolSmartSearchRequest,
  options?: { enabled?: boolean }
) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  const hasJd = !!request.jobDescription?.trim();

  return useQuery({
    queryKey: businessKeys.poolSmartSearch(request),
    queryFn: () => businessService.smartSearchPool(request),
    enabled: options?.enabled ?? hasJd,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Type-ahead pool facet options (BL-207). Keyed on field + query so each search
 * term caches independently; the Search-tab dropdowns debounce the query and
 * only fetch while open. Ranked by frequency; empty query = top defaults.
 */
export function usePoolFacetSearch(
  field: PoolFacetField,
  q: string,
  options?: { enabled?: boolean }
) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.poolFacetOptions(field, q),
    queryFn: () => businessService.searchPoolFacet(field, q),
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
}

/** Saved pool searches (BL-207) — tenant-scoped named JD + skill filters. */
export function usePoolSavedSearches(options?: { enabled?: boolean }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.poolSavedSearches(),
    queryFn: () => businessService.listSavedPoolSearches(),
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreatePoolSavedSearch() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Parameters<typeof businessService.createSavedPoolSearch>[0]) =>
      businessService.createSavedPoolSearch(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.poolSavedSearches() });
    },
  });
}

export function useDeletePoolSavedSearch() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => businessService.deleteSavedPoolSearch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.poolSavedSearches() });
    },
  });
}

export function usePoolTalent(id: string) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.poolTalent(id),
    queryFn: () => businessService.getPoolTalent(id),
    enabled: !!id,
  });
}

export function useAddPoolTalent() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { talentId: string; cohortId: string }) =>
      businessService.addPoolTalentToPipeline(input.talentId, { cohortId: input.cohortId }),
    onSuccess: () => {
      // Adding from the Pool creates a DRAFT invitation (BL-207), so refresh
      // both the business namespace AND the invitations list (the Acquisition
      // column reads the latter).
      queryClient.invalidateQueries({ queryKey: businessKeys.all });
      queryClient.invalidateQueries({ queryKey: talentInvitationKeys.all });
    },
  });
}

/**
 * Fetch a fresh presigned CV URL on demand (BL-207). A mutation, not a query,
 * because presigned URLs expire — the caller triggers it on click, then opens
 * the returned URL.
 */
export function usePoolTalentCvUrl() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (talentId: string) => businessService.getPoolTalentCvUrl(talentId),
  });
}

/** Bulk add pool talents to Acquisition under one cohort (BL-207). */
export function useAddPoolTalentsBulk() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { talentIds: string[]; cohortId: string }) =>
      businessService.addPoolTalentsBulk(input),
    onSuccess: () => {
      // Draft invitations (BL-207) — refresh the invitations list too.
      queryClient.invalidateQueries({ queryKey: businessKeys.all });
      queryClient.invalidateQueries({ queryKey: talentInvitationKeys.all });
    },
  });
}

/** Tonnie-internal full-profile Pool import (BL-207). */
export function useImportPoolTalents() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Parameters<typeof businessService.importPoolTalents>[0]) =>
      businessService.importPoolTalents(input),
    onSuccess: () => {
      // New pool rows appear in the overview + search results.
      queryClient.invalidateQueries({ queryKey: businessKeys.all });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// AFFILIATE PROGRAM HOOKS (BL-213)
// ═══════════════════════════════════════════════════════════════════════

export function useAffiliateStatus() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery<{ enrolled: boolean }>({
    queryKey: businessKeys.affiliateStatus(),
    queryFn: () => businessService.getAffiliateStatus(),
    staleTime: 1000 * 60 * 10,
  });
}

export function useAffiliateOverview(params?: AffiliateOverviewParams) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery<AffiliateOverview>({
    queryKey: businessKeys.affiliateOverview(params),
    queryFn: () => businessService.getAffiliateOverview(params),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Tonnie-wide cross-tenant affiliate overview (B10). Restricted to Tonnie staff
 * (internal tenant) server-side — gate the UI to `TONNIE_INTERNAL_TENANT_ID` and
 * pass `enabled` so an ordinary tenant never fires the 403.
 */
export function useAffiliatePlatformOverview(options?: { enabled?: boolean }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery<AffiliatePlatformOverview>({
    queryKey: businessKeys.affiliatePlatformOverview(),
    queryFn: () => businessService.getAffiliatePlatformOverview(),
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5,
  });
}

export function useAffiliateRedemptions(params?: AffiliateRedemptionParams) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery<AffiliateRedemptionList>({
    queryKey: businessKeys.affiliateRedemptions(params),
    queryFn: () => businessService.getAffiliateRedemptions(params),
    staleTime: 1000 * 60 * 2,
  });
}

/** Download the filtered redemptions as a CSV blob (whole dataset, not paged). */
export function useExportAffiliateRedemptions() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useMutation<Blob, Error, AffiliateExportParams | undefined>({
    mutationFn: (params) => businessService.getAffiliateRedemptionsCsv(params),
  });
}

export function useAffiliatePayouts() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery<AffiliatePayout[]>({
    queryKey: businessKeys.affiliatePayouts(),
    queryFn: () => businessService.getAffiliatePayouts(),
    staleTime: 1000 * 60 * 2,
  });
}

export function useRequestAffiliatePayout() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => businessService.requestAffiliatePayout(),
    onSuccess: () => {
      // Balance drops (reserved) and a new payout row appears. Invalidate the
      // overview PREFIX so every filtered variant refetches.
      queryClient.invalidateQueries({
        queryKey: [...businessKeys.all, 'affiliate-overview'],
      });
      queryClient.invalidateQueries({ queryKey: businessKeys.affiliatePayouts() });
    },
  });
}

/** Cancel a still-pending payout — releases the reserved balance. */
export function useCancelAffiliatePayout() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payoutId: string) => businessService.cancelAffiliatePayout(payoutId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...businessKeys.all, 'affiliate-overview'],
      });
      queryClient.invalidateQueries({ queryKey: businessKeys.affiliatePayouts() });
    },
  });
}

/**
 * Upload a payout invoice: mint a presigned PUT, upload the file directly to
 * storage, then attach the key. The bytes never transit platform-api.
 */
export function useUploadAffiliateInvoice() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { payoutId: string; file: File }) => {
      const { uploadUrl, fileKey } = await businessService.createAffiliateInvoiceUploadUrl(
        input.payoutId,
        { fileName: input.file.name, contentType: input.file.type || 'application/pdf' }
      );
      const put = await fetch(uploadUrl, {
        method: 'PUT',
        body: input.file,
        headers: { 'Content-Type': input.file.type || 'application/pdf' },
      });
      if (!put.ok) throw new Error(`Invoice upload failed (${put.status})`);
      return businessService.attachAffiliateInvoice(input.payoutId, fileKey);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.affiliatePayouts() });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 4: DASHBOARD & ACTIVITY HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function useDashboardData(filters?: { programId?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.dashboard(filters),
    queryFn: () => businessService.getDashboard(filters),
    staleTime: 1000 * 60 * 5,
  });
}

export function useActivity(params?: { limit?: number; since?: string }) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.activity(params),
    queryFn: () => businessService.getActivity(params),
  });
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 5: TEAM & SETTINGS HOOKS
// ═══════════════════════════════════════════════════════════════════════

export function useInviteTeamMember() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (data: {
      email: string;
      firstName?: string;
      lastName?: string;
      role?: string;
      department?: string;
    }) => businessService.inviteTeamMember(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.team });
    },
  });
}

export function useRemoveTeamMember() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (userId: string) => businessService.removeTeamMember(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.team });
    },
  });
}

export function useTeamInvitations() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: [...businessKeys.team, 'invitations'] as const,
    queryFn: () => businessService.listTeamInvitations(),
  });
}

export function useResendTeamInvitation() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (invitationId: string) => businessService.resendTeamInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.team });
    },
  });
}

export function useRevokeTeamInvitation() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (invitationId: string) => businessService.revokeTeamInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.team });
    },
  });
}

/** A team invitation, read by its link token before sign-in (public route). */
export function useTeamInvitationInfo(token: string | null) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: [...businessKeys.team, 'invitation-info', token] as const,
    queryFn: () => businessService.getTeamInvitationInfo(token as string),
    enabled: !!token,
    // A refusal (not found, expired) is an answer, not a blip to retry.
    retry: false,
  });
}

export function useAcceptTeamInvitation() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (token: string) => businessService.acceptTeamInvitation(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.team });
    },
  });
}

export function useTenantSettings() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.tenantSettings(),
    queryFn: () => businessService.getTenantSettings(),
    retry: false, // Don't retry on 404 (new tenant has no settings yet)
    // Tenant settings is the backing store for B2B onboarding. The
    // onboarding wizard writes via a DIFFERENT query key, so even with
    // cross-invalidation React Query may briefly serve the stale
    // cached row on the next dashboard mount. Force a fresh network
    // fetch on every mount + drop the cache entry when the last
    // observer unmounts, so the dashboard NEVER renders a pre-save
    // snapshot after the user completes the wizard.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });
}

export function useUpdateTenantSettings() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (data: Partial<TenantSettings>) => businessService.updateTenantSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.tenantSettings() });
      // CROSS-INVALIDATE: onboarding progress reads from the same
      // underlying tenant_settings row. Keep both caches in sync so a
      // QuickEdit from the dashboard is immediately reflected when the
      // user re-opens the onboarding wizard.
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'progress'] });
      queryClient.invalidateQueries({ queryKey: ['business', 'dashboard'] });
    },
  });
}

/**
 * Delete the caller's tenant (organization) — admin-only.
 *
 * Clears every cached query in the process: once the server returns
 * success, all of this user's org-scoped data no longer exists, and
 * they are about to be signed out. Invalidating individual query keys
 * would be both incomplete (too many slices to enumerate) and pointless
 * (we're logging out). `queryClient.clear()` is the correct hammer.
 *
 * The caller is responsible for kicking off `clerk.signOut()` in the
 * component layer — this hook stays pure.
 */
export function useDeleteTenant() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);

  return useMutation({
    mutationFn: (confirmationName: string) => businessService.deleteTenant(confirmationName),
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

/**
 * Resolve a company domain into a structured profile for the onboarding
 * wizard. Returns `null` (no fetch) when `domain` is null — used to gate
 * the call behind the feature flag and skip personal email domains.
 *
 * When `retryNonce` is > 0 the request is sent with `force: true`, which
 * tells the backend to skip its 90-day cache and re-run the full
 * provider chain (Brandfetch → BRAIN LLM → heuristics). The nonce is
 * included in the queryKey so React Query fires a fresh fetch each time
 * the caller bumps it — the intended pattern for the wizard's "Retry
 * auto-detect" button.
 */
export function useDomainEnrichment(domain: string | null, retryNonce = 0) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: ['business', 'enrichment', domain, retryNonce],
    queryFn: () =>
      businessService.enrichDomain(domain as string, retryNonce > 0 ? { force: true } : undefined),
    enabled: !!domain,
    // Client-side cache 1 day (server side caches 90 days).
    staleTime: 1000 * 60 * 60 * 24,
    retry: false,
  });
}

export function useAuditLogs(filters?: {
  action?: AuditAction;
  userId?: string;
  /** ISO date string — inclusive lower bound on `createdAt`. */
  startDate?: string;
  /** ISO date string — inclusive upper bound on `createdAt`. */
  endDate?: string;
  limit?: number;
  offset?: number;
}) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.auditLogs(filters),
    queryFn: () => businessService.getAuditLogs(filters),
  });
}

export function useBusinessMeshScores(talentIds: string[]) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);

  return useQuery({
    queryKey: businessKeys.meshScores(talentIds),
    queryFn: () => businessService.getMeshScores(talentIds),
    enabled: talentIds.length > 0,
  });
}

// MESSAGE HOOKS — defined in useMessages.ts (canonical location)
// Re-exported from there via hooks/index.ts

// ═══════════════════════════════════════════════════════════════════════
// CATALOGS (Radar Tooltips, 2026-Q3)
//
// Both hooks treat the catalog as session-stable: a single fetch per
// page lifetime drives every radar hover bubble across drawer opens.
// `staleTime: Infinity` defeats TanStack Query's normal refetch
// heuristics; `gcTime: 24h` keeps the payload memo'd well past the
// typical admin session length. The backend layer (1h TTL keyed by
// tenantId) handles Brain config rotation on the platform-api side —
// the FE only re-fetches on a hard reload or an explicit
// `invalidateQueries({ queryKey: businessKeys.archetypeCatalog() })`.
// ═══════════════════════════════════════════════════════════════════════

/**
 * Cultural Risk Archetype catalog (`name → entry`). Drives the
 * Archetypes radar hover bubble in `TalentDetailVetting`. Returns
 * `undefined` while loading; consumers should guard with `?.[name]`
 * lookups and fall back to a plain axis label on miss.
 */
export function useArchetypeCatalog() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  return useQuery<ArchetypeCatalog>({
    queryKey: businessKeys.archetypeCatalog(),
    queryFn: () => businessService.getArchetypeCatalog(),
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
  });
}

/**
 * Evaluation Pillar catalog (`code → entry`). Drives the Pillars radar
 * hover bubble in `TalentDetailVetting`. Same session-stable cache
 * profile as `useArchetypeCatalog`.
 */
export function usePillarCatalog() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  return useQuery<PillarCatalog>({
    queryKey: businessKeys.pillarCatalog(),
    queryFn: () => businessService.getPillarCatalog(),
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
  });
}

// ═══════════════════════════════════════════════════════════════════════
// OBJECTIVES-BASED FEEDBACK (BL-125)
// ═══════════════════════════════════════════════════════════════════════

/** Talents (visible to this business) with an OPEN objective-feedback cycle. */
export function useObjectiveFeedbackDue() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  return useQuery({
    queryKey: businessKeys.objectiveFeedbackDue(),
    queryFn: () => businessService.getObjectiveFeedbackDue(),
  });
}

/** Company-wide objectives achievement rollup (for the Objectives dashboard). */
export function useObjectivesOverview() {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  return useQuery({
    queryKey: businessKeys.objectivesOverview(),
    queryFn: () => businessService.getObjectivesOverview(),
  });
}

/**
 * Business view of ONE journey's objectives + current cycle + feedback entries.
 * Each journey holds its own objectives (X7): the card or drawer names its
 * journey, and the hook stays idle until it is known.
 */
export function useTalentObjectives(talentId: string, journeyId: string | undefined) {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  return useQuery({
    queryKey: businessKeys.talentObjectives(talentId, journeyId ?? null),
    queryFn: () => businessService.getTalentObjectives(talentId, requireJourney(journeyId)),
    enabled: !!talentId && !!journeyId,
  });
}

/** The calling provider (admin / team member) submits objective feedback on the journey. */
export function useSubmitTalentObjectiveFeedback(talentId: string, journeyId: string | undefined) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);
  return useMutation({
    mutationFn: (input: SubmitObjectiveFeedbackInput) =>
      businessService.submitTalentObjectiveFeedback(talentId, requireJourney(journeyId), input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessKeys.talentObjectives(talentId, journeyId ?? null),
      });
      queryClient.invalidateQueries({ queryKey: businessKeys.objectiveFeedbackDue() });
    },
  });
}

/** HR overrides one journey's baseline/target for an objective (hybrid rule). */
export function useSetTalentObjectiveTarget(talentId: string, journeyId: string | undefined) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);
  return useMutation({
    mutationFn: ({
      objectiveId,
      input,
    }: {
      objectiveId: string;
      input: { baselineValue?: number | null; targetValue?: number | null };
    }) =>
      businessService.setTalentObjectiveTarget(
        talentId,
        requireJourney(journeyId),
        objectiveId,
        input
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessKeys.talentObjectives(talentId, journeyId ?? null),
      });
    },
  });
}
